import React, { useState } from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid,
  HStack, Button, Icon, Flex,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Divider, Avatar,
} from "@chakra-ui/react";
import {
  FaArrowRight, FaCheckCircle, FaSearch, FaFlask, FaTools,
  FaTint, FaHeart, FaLeaf, FaShieldAlt, FaHome, FaBuilding,
  FaCouch, FaQuoteLeft, FaStar, FaWater,
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY       = "#141C49";
const WATER_BLUE = "#6FAFD6";
const DEEP_AQUA  = "#2C7DA0";
const ICE_MIST   = "#EAF3FB";

const W = (n: number | string) => `/assets/water-assets/asset%20${n}`;

const WaterHome: React.FC = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
    {
      name: "Kaine & Niomi",
      label: "Homeowners, Laverton, Melbourne",
      avatar: W("6.jpeg"),
      quote: "We were the first home in Victoria to have OneWater installed, and honestly we weren't sure what to expect. The difference in the water was immediate — the taste, the way it felt on our skin in the shower. We stopped buying bottled water within a week.",
    },
    {
      name: "Paul & Madeleine",
      label: "Homeowners, Bundoora, Melbourne",
      avatar: W("7.jpeg"),
      quote: "The assessment process was nothing like we expected — no pushy sales, just a genuinely useful conversation about our water. We felt informed rather than sold to. The system has been running flawlessly for over a year.",
    },
    {
      name: "Oliver Thompson",
      label: "Apartment Owner, Brisbane",
      avatar: W("8.jpeg"),
      quote: "I didn't think an apartment could have proper filtration without strata involvement. The team knew exactly how to handle it. The installation was compact, clean, and done in a morning.",
    },
    {
      name: "Marcus Lee",
      label: "Business Owner, Perth",
      avatar: null,
      quote: "We had the office system installed after one of the staff raised concerns about the water quality. The whole process — assessment through to installation — was professional and completely non-disruptive.",
    },
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/water"
        fallbackTitle="One Water Australia — Better Water Starts With Understanding Yours"
        fallbackDescription="Your water filtration providers, without the stress. Free in-home assessment, tailored recommendations, professional installation and ongoing support across Australia."
      />
      <NavbarWithCallToAction />

      {/* ── Hero — full-width split layout: text left, image bleeding to screen edge ── */}
      <Box bg="white" overflow="hidden">
        <SimpleGrid columns={{ base: 1, md: 2 }} alignItems="center" minH={{ md: "85vh" }}>
          {/* Left — text, aligned to the container.xl centre line */}
          <Flex justify={{ md: "flex-end" }} px={{ base: 4, md: 8 }} py={{ base: 16, md: 0 }}>
            <VStack
              align="flex-start"
              spacing={7}
              py={{ base: 8, md: 20 }}
              pr={{ md: 12 }}
              maxW={{ md: "640px" }}
              w="full"
            >
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="widest"
                textTransform="uppercase"
                color={WATER_BLUE}
              >
                One Water Australia
              </Text>
              <Heading
                as="h1"
                fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
                fontWeight="black"
                color={NAVY}
                lineHeight="1.08"
                letterSpacing="-0.02em"
              >
                Better Water Starts With Understanding Yours
              </Heading>
              <Text fontSize={{ base: "lg", md: "xl" }} color="gray.600" lineHeight="1.8" maxW="lg">
                Book a free water assessment — no obligation, no pressure. Whether that leads to a system, or just better information, you'll be better off either way.
              </Text>
              <HStack spacing={4} flexWrap="wrap">
                <Button
                  as={RouterLink}
                  to="/contact"
                  bg={NAVY}
                  color="white"
                  size="lg"
                  fontWeight="bold"
                  rightIcon={<FaArrowRight />}
                  _hover={{ bg: DEEP_AQUA, transform: "translateY(-1px)" }}
                  transition="all 0.2s"
                  px={8}
                >
                  Book a Free Assessment
                </Button>
                <Button
                  as={RouterLink}
                  to="/water/blog/tap-water-safe-australia"
                  variant="outline"
                  borderColor={WATER_BLUE}
                  color={NAVY}
                  size="lg"
                  _hover={{ bg: ICE_MIST, borderColor: DEEP_AQUA }}
                  transition="all 0.2s"
                >
                  Is Tap Water Safe?
                </Button>
              </HStack>
            </VStack>
          </Flex>

          {/* Right — hero image, full bleed to screen edge */}
          <Box
            position="relative"
            h={{ base: "360px", md: "100%" }}
            minH={{ md: "85vh" }}
            overflow="hidden"
          >
              <Box
                as="img"
                src={W("2.webp")}
                alt="Hand holding a glass of clean filtered water with lemon"
                position="absolute"
                top={0}
                right={0}
                h="full"
                w="full"
                objectFit="cover"
                objectPosition="center"
              />
              {/* Soft gradient blending into white on the left */}
              <Box
                position="absolute"
                top={0} left={0} bottom={0}
                w="30%"
                bgGradient="linear(to-r, white, transparent)"
                display={{ base: "none", md: "block" }}
              />
          </Box>
        </SimpleGrid>
      </Box>

      {/* ── Value proposition — "without the stress" ── */}
      <Box bg={ICE_MIST} py={{ base: 16, md: 20 }} borderTop={`2px solid ${WATER_BLUE}30`} borderBottom={`2px solid ${WATER_BLUE}30`}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            {/* Heading + intro text */}
            <VStack spacing={5} textAlign="center" maxW="3xl">
              <Heading size="xl" color={NAVY}>Your water filtration providers, without the stress</Heading>
              <Text fontSize="lg" color="gray.600" lineHeight="1.8">
                We take an assessment-first approach — visiting your home, testing your water, and giving you a clear picture of what's actually in it before recommending anything. No pressure. No jargon. Just the right answer for your water.
              </Text>
            </VStack>

            {/* Center image */}
            <Box
              borderRadius="2xl"
              overflow="hidden"
              w={{ base: "full", md: "60%" }}
              h={{ base: "260px", md: "380px" }}
              boxShadow="xl"
              mx="auto"
              position="relative"
            >
              <Box
                as="img"
                src={W("12.jpeg")}
                alt="Family using clean filtered water in their kitchen"
                w="full"
                h="full"
                objectFit="cover"
                objectPosition="center top"
              />
            </Box>

            {/* Three promises */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
              {[
                {
                  icon: FaSearch,
                  title: "Free In-Home Water Assessment",
                  body: "A specialist visits your home, tests your water on-site, and gives you a plain-English summary — at no cost and with no obligation.",
                },
                {
                  icon: FaFlask,
                  title: "Tailored Filtration Recommendations",
                  body: "We match the solution to your specific water, your property, and your household — not to a product we need to move.",
                },
                {
                  icon: FaTools,
                  title: "Professional Installation & Ongoing Support",
                  body: "Our team handles everything from installation through to ongoing servicing, filter replacements, and long-term support.",
                },
              ].map((item) => (
                <VStack
                  key={item.title}
                  p={7}
                  bg="white"
                  borderRadius="xl"
                  spacing={4}
                  align="flex-start"
                  border="1px solid"
                  borderColor={`${WATER_BLUE}40`}
                  boxShadow="sm"
                  _hover={{ borderColor: WATER_BLUE, boxShadow: "md" }}
                  transition="all 0.3s"
                >
                  <Icon as={item.icon} boxSize={9} color={WATER_BLUE} />
                  <Heading size="sm" color={NAVY}>{item.title}</Heading>
                  <Text color="gray.600" fontSize="sm" lineHeight="1.7">{item.body}</Text>
                </VStack>
              ))}
            </SimpleGrid>

            <Button
              as={RouterLink}
              to="/water/water-filtration"
              variant="link"
              color={DEEP_AQUA}
              rightIcon={<FaArrowRight />}
              fontWeight="semibold"
              _hover={{ color: NAVY }}
            >
              How it works
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* ── Testimonials ── */}
      <Box bg="white" py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <Heading size="xl" color={NAVY} textAlign="center">What People Notice When They Switch</Heading>

            {/* Featured / expanded testimonial */}
            <Box
              bg={ICE_MIST}
              borderRadius="2xl"
              p={{ base: 8, md: 12 }}
              border="1px solid"
              borderColor={`${WATER_BLUE}40`}
              w="full"
              maxW="3xl"
              mx="auto"
            >
              <Icon as={FaQuoteLeft} color={WATER_BLUE} boxSize={7} mb={4} opacity={0.7} />
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                color={NAVY}
                lineHeight="1.8"
                fontStyle="italic"
                mb={6}
              >
                "{testimonials[activeTestimonial].quote}"
              </Text>
              <HStack spacing={4}>
                {testimonials[activeTestimonial].avatar ? (
                  <Box
                    w={14} h={14}
                    borderRadius="full"
                    overflow="hidden"
                    border="2px solid"
                    borderColor={WATER_BLUE}
                    flexShrink={0}
                  >
                    <Box
                      as="img"
                      src={testimonials[activeTestimonial].avatar!}
                      alt={testimonials[activeTestimonial].name}
                      w="full" h="full"
                      objectFit="cover"
                      objectPosition="center top"
                    />
                  </Box>
                ) : (
                  <Box
                    w={14} h={14}
                    borderRadius="full"
                    bg={DEEP_AQUA}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Text color="white" fontWeight="800" fontSize="lg">
                      {testimonials[activeTestimonial].name[0]}
                    </Text>
                  </Box>
                )}
                <VStack align="start" spacing={0}>
                  <Text fontWeight="700" color={NAVY}>{testimonials[activeTestimonial].name}</Text>
                  <Text fontSize="sm" color="gray.500">{testimonials[activeTestimonial].label}</Text>
                  <HStack spacing="2px" mt={1}>
                    {[...Array(5)].map((_, i) => <Icon key={i} as={FaStar} color={WATER_BLUE} boxSize={3} />)}
                  </HStack>
                </VStack>
              </HStack>
            </Box>

            {/* Four profile cards — click to expand */}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} w="full">
              {testimonials.map((t, i) => (
                <Box
                  key={t.name}
                  onClick={() => setActiveTestimonial(i)}
                  cursor="pointer"
                  bg={activeTestimonial === i ? ICE_MIST : "white"}
                  borderRadius="xl"
                  p={5}
                  border="2px solid"
                  borderColor={activeTestimonial === i ? WATER_BLUE : "gray.100"}
                  _hover={{ borderColor: WATER_BLUE, bg: ICE_MIST }}
                  transition="all 0.2s"
                  textAlign="center"
                >
                  <Flex justify="center" mb={3}>
                    {t.avatar ? (
                      <Box
                        w={14} h={14}
                        borderRadius="full"
                        overflow="hidden"
                        border="2px solid"
                        borderColor={activeTestimonial === i ? WATER_BLUE : "gray.200"}
                        mx="auto"
                      >
                        <Box
                          as="img"
                          src={t.avatar}
                          alt={t.name}
                          w="full" h="full"
                          objectFit="cover"
                          objectPosition="center top"
                        />
                      </Box>
                    ) : (
                      <Box
                        w={14} h={14}
                        borderRadius="full"
                        bg={DEEP_AQUA}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mx="auto"
                      >
                        <Text color="white" fontWeight="800" fontSize="lg">{t.name[0]}</Text>
                      </Box>
                    )}
                  </Flex>
                  <Text fontWeight="700" color={NAVY} fontSize="sm">{t.name}</Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>{t.label}</Text>
                </Box>
              ))}
            </SimpleGrid>

            {/* Money-back guarantee */}
            <Box
              maxW="lg"
              mx="auto"
              p={5}
              bg={ICE_MIST}
              borderRadius="xl"
              border="1px solid"
              borderColor={`${WATER_BLUE}40`}
              textAlign="center"
            >
              <Text fontWeight="700" color={NAVY} fontSize="md" mb={1}>Thirty Day Money Back Guarantee</Text>
              <Text fontSize="sm" color="gray.600">Applies to hardware only. T&Cs apply.</Text>
              <Button
                as={RouterLink}
                to="/water/water-filtration"
                variant="link"
                color={DEEP_AQUA}
                fontSize="sm"
                mt={2}
                _hover={{ color: NAVY }}
              >
                Warranty and satisfaction →
              </Button>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* ── Where Do You Want Better Water? — product categories ── */}
      <Box bg="gray.50" py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={WATER_BLUE}>
              Our Solutions
            </Text>
            <Heading size="xl" color={NAVY}>Where Do You Want Better Water?</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              Every property is different. We start with your water, not a product catalogue.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {[
              {
                icon: FaHome,
                label: "RESIDENTIAL",
                title: "Whole-Home Filtration",
                img: W("9.webp"),
                imgAlt: "Father and son using clean filtered water at home",
                points: [
                  "Treats water at the mains — covers every tap",
                  "Tailored to household size and daily usage",
                  "Removes chlorine, sediment, heavy metals, contaminants",
                ],
                href: "/water/water-filters",
              },
              {
                icon: FaBuilding,
                label: "APARTMENT",
                title: "Point-of-Use Systems",
                img: W("11.jpeg"),
                imgAlt: "Child drinking clean filtered water at home",
                points: [
                  "Point-of-use systems for apartment plumbing",
                  "No strata approval required in most cases",
                  "Same quality filtration adapted for compact spaces",
                ],
                href: "/water/water-purifiers",
              },
              {
                icon: FaCouch,
                label: "OFFICE",
                title: "Commercial & Office",
                img: W("28.webp"),
                imgAlt: "OneWater filtration system professionally installed",
                points: [
                  "Systems sized for office and small commercial environments",
                  "Covers kitchen, bathroom, and client-facing water points",
                  "Minimal operational disruption during installation",
                ],
                href: "/water/water-filtration",
              },
            ].map((card) => (
              <Box
                key={card.label}
                bg="white"
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="md"
                border="1px solid"
                borderColor="gray.100"
                _hover={{ boxShadow: "xl", borderColor: WATER_BLUE, transform: "translateY(-4px)" }}
                transition="all 0.3s"
              >
                <Box h="220px" overflow="hidden" position="relative">
                  <Box as="img" src={card.img} alt={card.imgAlt} w="full" h="full" objectFit="cover" />
                  <Box position="absolute" top={4} left={4} bg={DEEP_AQUA} px={3} py={1} borderRadius="full">
                    <Text fontSize="xs" fontWeight="800" color="white" letterSpacing="wider">{card.label}</Text>
                  </Box>
                </Box>
                <Box p={7}>
                  <Icon as={card.icon} boxSize={8} color={WATER_BLUE} mb={3} />
                  <Heading size="md" color={NAVY} mb={4}>{card.title}</Heading>
                  <VStack align="start" spacing={2} mb={6}>
                    {card.points.map((pt) => (
                      <HStack key={pt} align="flex-start">
                        <Icon as={FaCheckCircle} color={DEEP_AQUA} boxSize={3.5} mt={0.5} flexShrink={0} />
                        <Text fontSize="sm" color="gray.600" lineHeight="1.6">{pt}</Text>
                      </HStack>
                    ))}
                  </VStack>
                  <Button
                    as={RouterLink}
                    to={card.href}
                    variant="link"
                    color={DEEP_AQUA}
                    rightIcon={<FaArrowRight />}
                    fontWeight="semibold"
                    _hover={{ color: NAVY }}
                  >
                    Find Out More
                  </Button>
                </Box>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── "Something about your water doesn't feel right" ── */}
      <Box bg="white" py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
            <Box
              borderRadius="2xl"
              overflow="hidden"
              h={{ base: "300px", md: "520px" }}
              boxShadow="xl"
            >
              <Box
                as="img"
                src={W("14.jpeg")}
                alt="Person filling pot at kitchen sink with filtered water"
                w="full" h="full"
                objectFit="cover"
                objectPosition="center"
              />
            </Box>
            <VStack align="flex-start" spacing={6}>
              <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={WATER_BLUE}>
                Sound Familiar?
              </Text>
              <Heading size="xl" color={NAVY} lineHeight="1.2">
                Something about your water doesn't feel quite right
              </Heading>
              <Text color="gray.600" fontSize="lg" lineHeight="1.8">
                Most people who contact us start with a feeling — not a diagnosis. Something about the taste, a mark on the sink, a question they can't quite answer. That's exactly the right point to start.
              </Text>
              <VStack align="start" spacing={4} w="full">
                {[
                  {
                    icon: FaTint,
                    title: "It just doesn't taste the way water should",
                    body: "When tap water doesn't taste right, people stop drinking it — reaching for bottled water instead. That's an expensive workaround for a solvable problem.",
                  },
                  {
                    icon: FaHeart,
                    title: "I want to know it's safe for my kids",
                    body: "You don't need to become a water scientist. You need someone to test your water, explain what's in it, and tell you clearly whether anything warrants action.",
                  },
                  {
                    icon: FaSearch,
                    title: "I've looked into it, but I can't make sense of it all",
                    body: "The information out there is genuinely conflicting. We cut through it — no agenda, no jargon, just a clear answer for your specific water.",
                  },
                ].map((item) => (
                  <HStack
                    key={item.title}
                    align="flex-start"
                    spacing={4}
                    p={4}
                    bg={ICE_MIST}
                    borderRadius="xl"
                    border="1px solid"
                    borderColor={`${WATER_BLUE}40`}
                    w="full"
                  >
                    <Icon as={item.icon} color={DEEP_AQUA} boxSize={5} mt={0.5} flexShrink={0} />
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="700" color={NAVY} fontSize="sm">{item.title}</Text>
                      <Text fontSize="sm" color="gray.600" lineHeight="1.7">{item.body}</Text>
                    </VStack>
                  </HStack>
                ))}
              </VStack>
              <Button
                as={RouterLink}
                to="/contact"
                bg={NAVY}
                color="white"
                size="lg"
                rightIcon={<FaArrowRight />}
                _hover={{ bg: DEEP_AQUA }}
                transition="all 0.2s"
              >
                Let's Have a Chat
              </Button>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Better Water. Better Life. — benefits 2x2 grid ── */}
      <Box bg={ICE_MIST} py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={WATER_BLUE}>
              The Benefits
            </Text>
            <Heading size="xl" color={NAVY}>Better Water. Better Life.</Heading>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {[
              {
                img: W("11.jpeg"),
                imgAlt: "Child drinking clean filtered water at kitchen bench",
                icon: FaHeart,
                title: "Health & Wellbeing",
                body: "Remove the contaminants your body doesn't need — chlorine, heavy metals, sediment — and give your family the cleanest possible daily hydration.",
                href: "/water/water-filters",
              },
              {
                img: W("12.jpeg"),
                imgAlt: "Couple preparing food together in a filtered-water home",
                icon: FaLeaf,
                title: "Our Environment",
                body: "Filtered tap water means less plastic. Fewer bottles purchased, fewer ending up in landfill. One household making the switch makes a measurable difference.",
                href: "/water/water-filtration",
              },
              {
                img: W("13.webp"),
                imgAlt: "Father and son at bathroom sink — softer filtered water for skin and hair",
                icon: FaTint,
                title: "Beauty & Skin",
                body: "Chlorine in tap water strips natural oils from skin and hair. Filtered water is noticeably softer — a difference many people notice within the first week.",
                href: "/water/water-purifiers",
              },
              {
                img: W("14.jpeg"),
                imgAlt: "Person using filtered water at kitchen sink for cooking",
                icon: FaShieldAlt,
                title: "Home Care",
                body: "Hard water and sediment damage kettles, coffee machines, dishwashers, and washing machines over time. Filtered water protects your appliances and reduces maintenance costs.",
                href: "/water/water-filtration",
              },
            ].map((card) => (
              <Box
                key={card.title}
                as={RouterLink}
                to={card.href}
                bg="white"
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="sm"
                border="1px solid"
                borderColor="gray.100"
                _hover={{ boxShadow: "xl", borderColor: WATER_BLUE, transform: "translateY(-3px)" }}
                transition="all 0.3s"
                display="flex"
                flexDirection={{ base: "column", lg: "row" }}
                textDecoration="none"
              >
                <Box
                  w={{ base: "full", lg: "220px" }}
                  h={{ base: "200px", lg: "auto" }}
                  flexShrink={0}
                  overflow="hidden"
                >
                  <Box as="img" src={card.img} alt={card.imgAlt} w="full" h="full" objectFit="cover" />
                </Box>
                <Box p={7} display="flex" flexDirection="column" justifyContent="center">
                  <Icon as={card.icon} boxSize={7} color={WATER_BLUE} mb={3} />
                  <Heading size="md" color={NAVY} mb={3}>{card.title}</Heading>
                  <Text fontSize="sm" color="gray.600" lineHeight="1.8">{card.body}</Text>
                </Box>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── The Process — 3 steps ── */}
      <Box bg="white" py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={WATER_BLUE}>
              How It Works
            </Text>
            <Heading size="xl" color={NAVY}>The Process</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl" lineHeight="1.8">
              Most water concerns begin with a question you can't quite answer. Our process is designed to answer it — clearly, without pressure, and starting with your actual water.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10}>
            {[
              {
                step: "01",
                img: W("4.webp"),
                imgAlt: "OneWater filtration unit mounted on exterior wall — professional installation",
                title: "WE ASSESS YOUR WATER",
                body: "A specialist visits your home, apartment, or workplace and conducts on-site water testing. You receive a clear, jargon-free summary of exactly what was found — no upselling, no obligation.",
              },
              {
                step: "02",
                img: W("28.webp"),
                imgAlt: "OneWater filtration system closeup — assessment and specification",
                title: "WE CONFIRM YOUR SYSTEM",
                body: "Recommendations are made based on your assessment results and the specific characteristics of your property. A tailored solution — not a generic product pushed from a catalogue.",
              },
              {
                step: "03",
                img: W("15.webp"),
                imgAlt: "OneWater installer delivering and setting up a filtration system",
                title: "WE INSTALL AND SUPPORT IT",
                body: "Our team handles the full installation. Ongoing servicing, filter replacements, and long-term support are all part of the relationship — not an afterthought.",
              },
            ].map((item) => (
              <Box
                key={item.step}
                bg="white"
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="md"
                border="1px solid"
                borderColor="gray.100"
                _hover={{ boxShadow: "xl", borderColor: WATER_BLUE }}
                transition="all 0.3s"
              >
                <Box h="220px" overflow="hidden" position="relative">
                  <Box as="img" src={item.img} alt={item.imgAlt} w="full" h="full" objectFit="cover" />
                  <Box
                    position="absolute"
                    top={4} left={4}
                    w={12} h={12}
                    bg={DEEP_AQUA}
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontWeight="black"
                    fontSize="lg"
                    color="white"
                    boxShadow={`0 2px 8px ${DEEP_AQUA}60`}
                  >
                    {item.step}
                  </Box>
                </Box>
                <Box p={7}>
                  <Heading size="sm" color={NAVY} mb={3} letterSpacing="wider">{item.title}</Heading>
                  <Text color="gray.600" fontSize="sm" lineHeight="1.8">{item.body}</Text>
                </Box>
              </Box>
            ))}
          </SimpleGrid>

          <VStack mt={12}>
            <Button
              as={RouterLink}
              to="/contact"
              bg={WATER_BLUE}
              color={NAVY}
              size="lg"
              fontWeight="bold"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#8DC5E0", transform: "translateY(-1px)" }}
              transition="all 0.2s"
            >
              How it Works
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* ── Built to do this properly ── */}
      <Box bg="gray.50" py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
            <VStack align="flex-start" spacing={6}>
              <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={WATER_BLUE}>
                About Us
              </Text>
              <Heading size="xl" color={NAVY} lineHeight="1.2">Built to do this properly</Heading>
              <Text color="gray.600" fontSize="lg" lineHeight="1.8">
                One Water Australia was founded on a straightforward belief: access to better water shouldn't require navigating pressure sales, confusing product comparisons, or guesswork. It should start with an honest assessment of your actual water.
              </Text>
              <Text color="gray.600" fontSize="md" lineHeight="1.8">
                We are part of One Group Australia — which means our commitment to accountability and genuine expertise runs through everything we do. Our assessors don't operate on commission. Our recommendations come from your results, not our margins.
              </Text>
              <SimpleGrid columns={2} spacing={4} w="full">
                {[
                  { label: "Assessment-First", desc: "We test before we recommend — always." },
                  { label: "No Commission Sales", desc: "Assessors operate independently of product margins." },
                  { label: "One Group Backed", desc: "Part of Australia's integrated Prosperity Engine." },
                  { label: "Long-Term Support", desc: "The same team installs, services, and supports." },
                ].map((item) => (
                  <Box key={item.label} p={4} bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.100">
                    <Text fontWeight="700" color={NAVY} mb={1} fontSize="sm">{item.label}</Text>
                    <Text fontSize="xs" color="gray.600" lineHeight="1.6">{item.desc}</Text>
                  </Box>
                ))}
              </SimpleGrid>
              <Button
                as={RouterLink}
                to="/contact"
                bg={NAVY}
                color="white"
                size="lg"
                rightIcon={<FaArrowRight />}
                _hover={{ bg: DEEP_AQUA }}
                transition="all 0.2s"
              >
                Let's Have a Chat
              </Button>
            </VStack>

            <Box borderRadius="2xl" overflow="hidden" h={{ base: "320px", md: "500px" }} boxShadow="2xl" position="relative">
              <Box
                as="img"
                src={W("15.webp")}
                alt="OneWater Australia installer setting up a home filtration system"
                w="full" h="full"
                objectFit="cover"
                objectPosition="center top"
              />
              <Box
                position="absolute"
                bottom={0} left={0} right={0}
                p={5}
                bgGradient={`linear(to-t, ${NAVY}CC, transparent)`}
              >
                <Text fontSize="xs" fontWeight="700" color="white" letterSpacing="wider" textTransform="uppercase">
                  Australian-owned &amp; operated
                </Text>
              </Box>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Ready CTA ── */}
      <Box bg={NAVY} py={16}>
        <Container maxW="container.xl" textAlign="center">
          <VStack spacing={6}>
            <Heading size="xl" color="white">Ready to find out what's in your water?</Heading>
            <Text fontSize="lg" color="whiteAlpha.800" maxW="xl" lineHeight="1.8">
              Book a free assessment — no obligation, no pressure. Whether that leads to a system, or just to better information, you'll be better off either way.
            </Text>
            <Button
              as={RouterLink}
              to="/contact"
              bg={WATER_BLUE}
              color={NAVY}
              size="lg"
              fontWeight="bold"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#8DC5E0", transform: "translateY(-1px)" }}
              transition="all 0.2s"
            >
              Let's Have a Chat
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* ── FAQ ── */}
      <Box bg="gray.50" py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={WATER_BLUE}>
              FAQs
            </Text>
            <Heading size="xl" color={NAVY}>Common Questions</Heading>
          </VStack>
          <Accordion allowMultiple maxW="3xl" mx="auto">
            {[
              { q: "What is whole-home water filtration?", a: "Whole-home filtration treats water at the point of entry into your property — meaning every tap, shower, and appliance in the home receives filtered water. Unlike under-sink or jug filters, there's no need to filter at individual points." },
              { q: "How is this different from under-sink or jug filters?", a: "Under-sink and jug filters only treat water at that specific point. Whole-home systems cover every tap in the house — including showers, laundry, and garden taps — and provide appliance protection that point-of-use filters can't deliver." },
              { q: "Will filtration reduce water pressure?", a: "Not when properly installed. Our systems are specified for Australian home water pressure and designed to maintain full flow. You won't notice a difference in pressure." },
              { q: "Is Australian tap water safe?", a: "Australian drinking water meets national safety guidelines — and that matters. But 'safe by guidelines' isn't the same as 'optimal for health.' Regional variation, ageing infrastructure, and contaminants not part of routine monitoring (like PFAS) mean the full picture is more complex than utilities typically communicate." },
              { q: "What's commonly found in tap water?", a: "Chlorine and chloramines (used for disinfection), sediment, minerals, heavy metals from plumbing, disinfection byproducts, and — in some regions — PFAS compounds and microplastics. The specific profile varies significantly by location and infrastructure." },
              { q: "How do I know if my water needs filtration?", a: "Common indicators include taste or smell issues, skin dryness after showering, limescale buildup on appliances, discolouration, or simply not knowing what's in your water. A free assessment is the most reliable way to get a clear answer." },
              { q: "Why choose One Water Australia?", a: "We lead with your water — not a product. Our assessors are not commissioned salespeople. We test first, recommend second, and provide long-term support through the same team that installs your system. Our systems are designed for Australian conditions." },
              { q: "Is the assessment really obligation-free?", a: "Yes. The assessment is a genuine service, not a sales technique. A specialist visits, tests your water, and gives you a clear summary of findings. If nothing warrants action, we'll tell you that." },
              { q: "Can filtered water help with skin and hair?", a: "Many customers notice an improvement in skin and hair condition after switching to filtered water, particularly where chlorine removal is involved. Individual results vary, but the reduction in harsh chemicals in shower water is real and measurable." },
              { q: "Does filtered water taste different?", a: "Most people notice a significant improvement in taste and smell after filtration — particularly where chlorine is present. Better-tasting water also tends to increase daily consumption and reduce reliance on bottled water." },
              { q: "What happens during installation?", a: "Our team handles everything: preparation, system integration, testing, and a full walk-through of how your system works. Installations are typically completed within a day and scheduled to minimise disruption." },
              { q: "How often do filters need replacing?", a: "Filter replacement schedules vary depending on your system design, water quality, and household usage. We'll advise on the right schedule for your specific system and provide ongoing support for replacements." },
              { q: "What are the ongoing costs?", a: "Ongoing costs depend on your system, filter type, and usage. We'll give you a clear picture of expected maintenance costs as part of the assessment and recommendation process — no surprises." },
              { q: "What happens if I move house?", a: "Many of our systems can be relocated to a new property. Discuss this with us during your assessment — we'll factor it into the recommendation where relevant." },
              { q: "Are your systems compliant and certified?", a: "Yes. Our systems are designed to Australian standards and relevant compliance requirements. We don't cut corners on certification — it's part of what it means to do this properly." },
            ].map((faq) => (
              <AccordionItem
                key={faq.q}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                mb={3}
                overflow="hidden"
              >
                <AccordionButton p={5} _hover={{ bg: "gray.50" }} _expanded={{ bg: "gray.50" }}>
                  <Box flex="1" textAlign="left">
                    <Text fontWeight="600" color={NAVY} fontSize="sm">{faq.q}</Text>
                  </Box>
                  <AccordionIcon color="gray.400" />
                </AccordionButton>
                <AccordionPanel pb={5} px={5} bg="white" borderTop="1px solid" borderColor="gray.100">
                  <Text fontSize="sm" color="gray.700" lineHeight="1.8">{faq.a}</Text>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Container>
      </Box>

      {/* ── Water quality deep-dive cards ── */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <VStack spacing={4} mb={10} textAlign="center">
          <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={WATER_BLUE}>
            Water Quality
          </Text>
          <Heading size="xl" color={NAVY}>Concerned about what's in your tap water?</Heading>
          <Text fontSize="lg" color="gray.600" maxW="3xl" lineHeight="1.8">
            Here's what the evidence actually says about the most common contaminants in Australian drinking water.
          </Text>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {[
            { icon: FaShieldAlt, title: "PFAS", sub: '"Forever chemicals"', body: "Detected near defence bases and industrial sites across Australia. Not part of standard utility monitoring — independent testing is the only way to know.", href: "/water/water-filtration" },
            { icon: FaFlask, title: "Chlorine & Byproducts", sub: "Disinfection chemicals", body: "Used to treat municipal water, but chlorine and chloramines form disinfection byproducts associated with long-term health concerns. Carbon filtration removes them effectively.", href: "/water/blog/chlorine-in-tap-water" },
            { icon: FaTint, title: "Fluoride", sub: "Added in most Australian areas", body: "The science on neurological effects at community doses is more contested than official bodies suggest. Reverse osmosis is the only residential method that reliably removes it.", href: "/water/water-purifiers" },
            { icon: FaWater, title: "Heavy Metals", sub: "From ageing plumbing", body: "Lead from pre-1989 plumbing solder and brass fittings can contaminate your tap water even when mains water is within guidelines. No safe dose exists for children.", href: "/water/blog/hard-water-australia" },
            { icon: FaSearch, title: "Microplastics", sub: "Unregulated — no ADWG guideline", body: "Found in Australian tap water, human blood, and tissue. No regulatory guideline means no monitoring obligation. Reverse osmosis provides the most effective removal.", href: "/water/water-purifiers" },
            { icon: FaLeaf, title: "Limescale & Hardness", sub: "Mineral buildup", body: "Hard water causes scale on appliances, shower screens, and kettles — shortening their lifespan and increasing maintenance costs. Common across much of Australia.", href: "/water/blog/hard-water-australia" },
          ].map((item) => (
            <Box
              key={item.title}
              as={RouterLink}
              to={item.href}
              bg="white"
              borderRadius="xl"
              p={7}
              border="1px solid"
              borderColor="gray.200"
              _hover={{ borderColor: WATER_BLUE, boxShadow: "md", transform: "translateY(-2px)" }}
              transition="all 0.3s"
              display="block"
              textDecoration="none"
            >
              <Icon as={item.icon} boxSize={8} color={WATER_BLUE} mb={3} />
              <Heading size="sm" color={NAVY} mb={1}>{item.title}</Heading>
              <Text fontSize="xs" fontWeight="600" color={DEEP_AQUA} mb={3}>{item.sub}</Text>
              <Text fontSize="sm" color="gray.600" lineHeight="1.7">{item.body}</Text>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* ── Final CTA ── */}
      <Box bg={NAVY} py={16}>
        <Container maxW="container.xl" textAlign="center">
          <VStack spacing={6}>
            <Heading size="xl" color="white">Understanding your water is the first step.</Heading>
            <Text fontSize="lg" color="whiteAlpha.800" maxW="xl" lineHeight="1.8">
              Book a free water assessment — no obligation, no pressure. Whether that leads to a system, or just to better information, you'll be better off either way.
            </Text>
            <Button
              as={RouterLink}
              to="/contact"
              bg={WATER_BLUE}
              color={NAVY}
              size="lg"
              fontWeight="bold"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#8DC5E0", transform: "translateY(-1px)" }}
              transition="all 0.2s"
            >
              Book a Free Water Assessment
            </Button>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default WaterHome;
